import { useWidget } from '@/_shared/context/widget.js';
import { useAddInConfig } from '@/_shared/hooks/add-in-config.js';
import { useClaimReportGuard } from '@/_shared/hooks/claim-report-guard.js';
import { useSyncStatus } from '@/_shared/hooks/sync-status.js';
import { useSafeSessionStorage } from '@/_shared/tools/storage.js';
import { Body1, Button, Spinner } from '@fluentui/react-components';
import { Navigate, Outlet, ScrollRestoration, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect } from 'react';
import { SyncStatusBar } from '../sync-status-bar/index.js';
import { SyncStatusFullscreen } from '../sync-status-fullscreen/index.js';
import { useStyles } from './_styles.js';
import { Sidebar } from './sidebar/index.js';

const SYNC_DISMISSED_KEY = 'sync-status-dismissed';

export function Layout() {
    const styles = useStyles();

    // Handle widget-based navigation from portal
    const navigate = useNavigate();
    const { mode, activeWidget } = useWidget();

    const config = useAddInConfig();
    const claimReportOnly = config.data?.body.access === 'claim-report';

    useEffect(() => {
        if (!activeWidget.id) {
            return;
        }

        // The portal does not know about the KSH restriction, so its widget
        // requests must not override the claim-report lock.
        if (claimReportOnly && activeWidget.id !== 'report-claim') {
            return;
        }

        navigate({ to: '/' + activeWidget.id });
    }, [activeWidget, claimReportOnly, navigate]);

    const guard = useClaimReportGuard();

    // Sync status state management
    const syncStatus = useSyncStatus();
    const [syncDismissed, setSyncDismissed] = useSafeSessionStorage(SYNC_DISMISSED_KEY, () => false);

    const handleSyncDismiss = useCallback(() => {
        setSyncDismissed(true);
    }, [setSyncDismissed]);

    if (guard.state === 'loading') {
        return (
            <div className={styles.centered}>
                <Spinner size="large" />
            </div>
        );
    }

    if (guard.state === 'error') {
        return (
            <div className={styles.centered}>
                <Body1>Die Anwendung konnte nicht geladen werden. Bitte versuchen Sie es erneut.</Body1>
                <Button appearance="primary" onClick={() => config.refetch()}>
                    Erneut versuchen
                </Button>
            </div>
        );
    }

    if (guard.state === 'redirect') {
        return <Navigate to={guard.to} replace />;
    }

    // Claim-report-only users never see Schadenmanager data, so they are not
    // held behind the claims sync either.
    const showSyncStatus = typeof syncStatus === 'number' && !claimReportOnly;

    // Render component
    return (
        <>
            <ScrollRestoration />

            {showSyncStatus && !syncDismissed ? (
                <SyncStatusFullscreen progress={syncStatus} onDismiss={handleSyncDismiss} />
            ) : (
                <div className={styles.container}>
                    {showSyncStatus && <SyncStatusBar progress={syncStatus} />}

                    {mode === 'standalone' && <Sidebar />}

                    <main className={styles.main}>
                        <Outlet />
                    </main>
                </div>
            )}
        </>
    );
}
